import type {
  InsuranceCarrier,
  CarrierStateMatrix,
  CampaignCarrierConfigInput,
  BulkCarrierConfigResult
} from '../types'
import type { APIResponse } from '@/shared/types'
import { apiService } from '@/shared/services/api'

export class InsuranceCarrierService {
  /**
   * Get all insurance carriers
   */
  static async getCarriers(includeInactive = false): Promise<APIResponse<InsuranceCarrier[]>> {
    try {
      const params = includeInactive ? '?includeInactive=true' : ''
      const response = await apiService.get(`/insurance-carriers${params}`)
      return response.data
    } catch (error: any) {
      console.error('Error fetching insurance carriers:', error)
      throw new Error(error.response?.data?.message || 'Failed to fetch insurance carriers')
    }
  }

  /**
   * Get insurance carrier by ID
   */
  static async getCarrierById(id: string): Promise<APIResponse<InsuranceCarrier>> {
    try {
      const response = await apiService.get(`/insurance-carriers/${id}`)
      return response.data
    } catch (error: any) {
      console.error('Error fetching insurance carrier:', error)
      throw new Error(error.response?.data?.message || 'Failed to fetch insurance carrier')
    }
  }

  /**
   * Create a new insurance carrier
   */
  static async createCarrier(data: Partial<InsuranceCarrier>): Promise<APIResponse<InsuranceCarrier>> {
    try {
      const response = await apiService.post('/insurance-carriers', data)
      return response.data
    } catch (error: any) {
      console.error('Error creating insurance carrier:', error)
      throw new Error(error.response?.data?.message || 'Failed to create insurance carrier')
    }
  }

  /**
   * Update an insurance carrier
   */
  static async updateCarrier(id: string, data: Partial<InsuranceCarrier>): Promise<APIResponse<InsuranceCarrier>> {
    try {
      const response = await apiService.put(`/insurance-carriers/${id}`, data)
      return response.data
    } catch (error: any) {
      console.error('Error updating insurance carrier:', error)
      throw new Error(error.response?.data?.message || 'Failed to update insurance carrier')
    }
  }

  /**
   * Delete an insurance carrier
   */
  static async deleteCarrier(id: string): Promise<APIResponse<void>> {
    try {
      const response = await apiService.delete(`/insurance-carriers/${id}`)
      return response.data
    } catch (error: any) {
      console.error('Error deleting insurance carrier:', error)
      throw new Error(error.response?.data?.message || 'Failed to delete insurance carrier')
    }
  }

  /**
   * Get carrier state matrix for a campaign
   */
  static async getCarrierStateMatrix(campaignId: string): Promise<APIResponse<CarrierStateMatrix>> {
    try {
      const response = await apiService.get(`/insurance-carriers/campaign/${campaignId}/matrix`)
      return response.data
    } catch (error: any) {
      console.error('Error fetching carrier state matrix:', error)
      throw new Error(error.response?.data?.message || 'Failed to fetch carrier state matrix')
    }
  }

  /**
   * Bulk upsert campaign carrier configurations
   */
  static async bulkUpsertCarrierConfigs(
    campaignId: string,
    configs: CampaignCarrierConfigInput[]
  ): Promise<APIResponse<BulkCarrierConfigResult>> {
    try {
      const response = await apiService.post(`/insurance-carriers/campaign/${campaignId}/bulk`, { configs })
      return response.data
    } catch (error: any) {
      console.error('Error saving carrier configurations:', error)
      throw new Error(error.response?.data?.message || 'Failed to save carrier configurations')
    }
  }
}

export default InsuranceCarrierService
